import { useState, type ReactNode } from "react";
import {
  Bell,
  ChevronLeft,
  ChevronRight,
  Search,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@vseh/ui";

/**
 * AppShell — общий каркас всех приложений монорепо.
 *
 * По §15 «Layouts» — top bar (56px), sidebar (240/64px), main, и
 * опциональная AI panel справа.
 *
 *  ┌──────────────────────────────────────────────────────────┐
 *  │  Top bar                                                  │
 *  ├────────────┬────────────────────────────┬─────────────────┤
 *  │            │                            │                  │
 *  │  Sidebar   │      Main                  │   AI panel      │
 *  │            │                            │   (опционален)  │
 *  └────────────┴────────────────────────────┴─────────────────┘
 *
 * AppShell — глупый: композирует то, что ему передают props'ами.
 * Маршрутизация и состояние — в приложении.
 */

export interface NavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  href: string;
  /** Бейдж справа: число (count) или "●" для неотложного. */
  badge?: number | "dot";
  badgeTone?: "neutral" | "ai" | "heritage" | "danger" | "success";
}

export interface NavGroup {
  /** Можно опустить — тогда раздел без заголовка. */
  label?: string;
  items: NavItem[];
}

export interface AppShellProps {
  /** Бренд / лого слева в top bar. */
  brand: ReactNode;
  /** Группы пунктов sidebar'а. Разделяются тонкой линией. */
  nav: NavGroup[];
  /** id активного пункта. */
  activePath: string;
  /** Callback при переходе. Реализация — в приложении. */
  onNavigate: (item: NavItem) => void;
  /** Контент top bar справа от поиска: meter, кнопки, аватар. */
  topBarTrailing?: ReactNode;
  /** Содержимое страницы. */
  children: ReactNode;
  /** Опциональная AI-панель справа (CRM-чат, multi-agent timeline). */
  aiPanel?: ReactNode;
  /** Состояние AI-панели — открыта/закрыта. */
  aiPanelOpen?: boolean;
  onAiPanelToggle?: (open: boolean) => void;
}

export function AppShell({
  brand,
  nav,
  activePath,
  onNavigate,
  topBarTrailing,
  children,
  aiPanel,
  aiPanelOpen = false,
  onAiPanelToggle,
}: AppShellProps): JSX.Element {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="flex min-h-screen flex-col bg-canvas text-base-900">
      {/* ---- Top bar ---- */}
      <header
        className={cn(
          "sticky top-0 z-sticky h-14 border-b border-base-200 bg-surface",
          "flex items-center gap-3 px-4",
        )}
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            aria-label={collapsed ? "Развернуть меню" : "Свернуть меню"}
            className="grid size-8 place-items-center rounded-md text-base-600 hover:bg-base-100"
          >
            {collapsed ? (
              <ChevronRight aria-hidden="true" className="size-4" />
            ) : (
              <ChevronLeft aria-hidden="true" className="size-4" />
            )}
          </button>
          <div className="font-display font-extrabold tracking-tight">{brand}</div>
        </div>

        <div className="ml-4 flex flex-1 items-center">
          <div
            className={cn(
              "flex h-8 w-full max-w-md items-center gap-2 rounded-md border border-base-200 bg-base-50 px-3",
              "focus-within:bg-surface focus-within:shadow-focus",
            )}
          >
            <Search aria-hidden="true" className="size-4 text-base-500" />
            <input
              type="search"
              placeholder="Найти технику, заявки, сделки... (Ctrl + K)"
              className="min-w-0 flex-1 bg-transparent text-sm text-base-900 outline-none placeholder:text-base-400"
            />
            <kbd className="rounded-xs bg-base-200 px-1.5 py-0.5 font-mono text-2xs text-base-600">
              ⌘K
            </kbd>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {topBarTrailing}
          {aiPanel && (
            <button
              type="button"
              onClick={() => onAiPanelToggle?.(!aiPanelOpen)}
              aria-pressed={aiPanelOpen}
              aria-label="Переключить AI-панель"
              className={cn(
                "grid size-8 place-items-center rounded-md transition-colors",
                aiPanelOpen
                  ? "bg-ai-50 text-ai-700"
                  : "text-base-600 hover:bg-base-100",
              )}
            >
              <Sparkles aria-hidden="true" className="size-4" />
            </button>
          )}
          <button
            type="button"
            aria-label="Уведомления"
            className="grid size-8 place-items-center rounded-md text-base-600 hover:bg-base-100"
          >
            <Bell aria-hidden="true" className="size-4" />
          </button>
        </div>
      </header>

      {/* ---- Body ---- */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside
          className={cn(
            // Sprint AQ — sidebar collapse is route-tier motion:
            // bigger gesture than a button hover, "the layout is
            // re-shaping" rather than "this element acknowledged you".
            "shrink-0 border-r border-base-200 bg-base-50 transition-[width] duration-route ease-route",
            collapsed ? "w-16" : "w-60",
          )}
        >
          <nav aria-label="Главная навигация" className="flex flex-col gap-2 p-2">
            {nav.map((group, idx) => (
              <div key={group.label ?? idx}>
                {group.label && !collapsed && (
                  <div className="mb-1 px-2 pt-2 text-2xs font-medium uppercase tracking-wide text-base-500">
                    {group.label}
                  </div>
                )}
                <ul className="flex flex-col gap-0.5">
                  {group.items.map((item) => (
                    <li key={item.id}>
                      <NavRow
                        item={item}
                        active={activePath === item.href}
                        collapsed={collapsed}
                        onClick={() => onNavigate(item)}
                      />
                    </li>
                  ))}
                </ul>
                {idx < nav.length - 1 && (
                  <div className="mx-2 my-2 h-px bg-base-200" />
                )}
              </div>
            ))}
          </nav>
        </aside>

        {/* Main */}
        <main className="flex-1 overflow-auto">
          <div className="mx-auto w-full max-w-wide px-6 py-6">{children}</div>
        </main>

        {/* AI panel */}
        {aiPanel && aiPanelOpen && (
          <aside
            className={cn(
              "w-[420px] shrink-0 border-l border-base-200 bg-surface",
              "overflow-auto",
            )}
          >
            {aiPanel}
          </aside>
        )}
      </div>
    </div>
  );
}

interface NavRowProps {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  onClick: () => void;
}

function NavRow({ item, active, collapsed, onClick }: NavRowProps): JSX.Element {
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      title={collapsed ? item.label : undefined}
      className={cn(
        "flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-sm transition-colors",
        active
          ? "bg-primary-50 font-medium text-primary-900"
          : "text-base-700 hover:bg-base-100 hover:text-base-900",
      )}
    >
      <Icon aria-hidden="true" className={cn("size-4 shrink-0", active && "text-primary-700")} />
      {!collapsed && (
        <>
          <span className="min-w-0 flex-1 truncate text-left">{item.label}</span>
          {item.badge != null && <Badge badge={item.badge} tone={item.badgeTone} />}
        </>
      )}
    </button>
  );
}

function Badge({
  badge,
  tone = "neutral",
}: {
  badge: number | "dot";
  tone?: NavItem["badgeTone"];
}): JSX.Element {
  const toneCls = {
    neutral:  "bg-base-200 text-base-700",
    ai:       "bg-ai-100 text-ai-800",
    heritage: "bg-heritage-100 text-heritage-800",
    danger:   "bg-danger-100 text-danger-800",
    success:  "bg-success-100 text-success-800",
  }[tone ?? "neutral"];

  if (badge === "dot") {
    return (
      <span
        aria-hidden="true"
        className={cn("size-1.5 shrink-0 rounded-full", {
          "bg-ai-500":       tone === "ai",
          "bg-heritage-500": tone === "heritage",
          "bg-danger-500":   tone === "danger",
          "bg-success-500":  tone === "success",
          "bg-base-400":     !tone || tone === "neutral",
        })}
      />
    );
  }
  return (
    <span
      className={cn(
        "inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-2xs tabular-nums",
        toneCls,
      )}
    >
      {badge}
    </span>
  );
}
